import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import SplashScreen from './components/SplashScreen/SplashScreen';
import ScrollToTopButton from './components/ScrollToTopButton';
import FloatingWhatsAppButton from './components/FloatingWhatsAppButton';

// Lazy load components for better performance
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Payment = lazy(() => import('./pages/Payment'));
const Profile = lazy(() => import('./pages/Profile'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const UploadContent = lazy(() => import('./pages/UploadContent'));
const CreateExam = lazy(() => import('./pages/CreateExam'));
const CreateContentExam = lazy(() => import('./pages/CreateContentExam'));
const MyContent = lazy(() => import('./pages/MyContent'));
const MyExams = lazy(() => import('./pages/MyExams'));
const InstructorEarnings = lazy(() => import('./pages/InstructorEarnings'));
const InstructorAnalytics = lazy(() => import('./pages/InstructorAnalytics'));
const Content = lazy(() => import('./pages/Content'));
const ContentDetail = lazy(() => import('./pages/ContentDetail'));
const Exams = lazy(() => import('./pages/Exams'));
const Progress = lazy(() => import('./pages/Progress'));
const TakeExam = lazy(() => import('./pages/TakeExam'));
const ExamResults = lazy(() => import('./pages/ExamResults'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Programmes = lazy(() => import('./pages/Programmes'));
const EventsUpdates = lazy(() => import('./pages/EventsUpdates'));
const FAQ = lazy(() => import('./pages/FAQ'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PaymentCallback = lazy(() => import('./pages/PaymentCallback'));
const MyReport = lazy(() => import('./pages/MyReport'));
const InstructorAgreement = lazy(() => import('./pages/InstructorAgreement'));

// Loading component
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3498db',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }}></div>
    <p style={{ color: '#666', fontSize: '14px' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading spinner while checking auth status
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
      {/* Splash Screen - Landing Page (for non-authenticated users) */}
      <Route 
        path="/" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <SplashScreen />} 
      />
      
      {/* Public routes (outside Layout) */}
      <Route
        path="about"
        element={<About />}
      />
      <Route
        path="services"
        element={<Services />}
      />
      <Route
        path="programmes"
        element={<Programmes />}
      />
      <Route
        path="events"
        element={<EventsUpdates />}
      />
      <Route
        path="faq"
        element={<FAQ />}
      />
      <Route
        path="login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route
        path="register"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Register />}
      />
      <Route
        path="forgot-password"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPassword />}
      />
      <Route
        path="reset-password"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPassword />}
      />
      
      {/* Protected routes with Layout */}
      <Route element={<Layout />}>
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment"
          element={
            <ProtectedRoute>
              <Payment />
            </ProtectedRoute>
          }
        />
        <Route
          path="payment/callback"
          element={
            <ProtectedRoute>
              <PaymentCallback />
            </ProtectedRoute>
          }
        />
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="privacy-policy"
          element={<PrivacyPolicy />}
        />
        <Route
          path="terms-of-service"
          element={<TermsOfService />}
        />
        <Route
          path="upload-content"
          element={
            <ProtectedRoute>
              <UploadContent />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor-agreement"
          element={
            <ProtectedRoute>
              <InstructorAgreement />
            </ProtectedRoute>
          }
        />
        <Route
          path="create-exam"
          element={
            <ProtectedRoute>
              <CreateExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-content"
          element={
            <ProtectedRoute>
              <MyContent />
            </ProtectedRoute>
          }
        />
        <Route
          path="my-exams"
          element={
            <ProtectedRoute>
              <MyExams />
            </ProtectedRoute>
          }
        />
        <Route
          path="content"
          element={
            <ProtectedRoute>
              <Content />
            </ProtectedRoute>
          }
        />
        <Route
          path="content/:id"
          element={
            <ProtectedRoute>
              <ContentDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="content/:id/create-exam"
          element={
            <ProtectedRoute>
              <CreateContentExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor/earnings"
          element={
            <ProtectedRoute>
              <InstructorEarnings />
            </ProtectedRoute>
          }
        />
        <Route
          path="instructor/analytics"
          element={
            <ProtectedRoute>
              <InstructorAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="exams"
          element={
            <ProtectedRoute>
              <Exams />
            </ProtectedRoute>
          }
        />
        <Route
          path="exams/:id/take"
          element={
            <ProtectedRoute>
              <TakeExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="progress"
          element={
            <ProtectedRoute>
              <Progress />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute>
              <MyReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="exam-results/:id"
          element={
            <ProtectedRoute>
              <ExamResults />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ScrollToTopButton />
        <FloatingWhatsAppButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
